import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import EmployeeRolesService, { EmployeeRoles, EmployeeRolesUpdatePayload } from '../services/EmployeeRolesService';
import WorkAreaService, { WorkArea } from '../../workareas/services/WorkAreaService';
import { normalizeNetworkError } from '../../../utils/errorUtils';
import TelemetryService from '../../../core/services/TelemetryService';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';

interface EmployeeRolesTabProps {
  employeeId: number;
  employeeStatus?: string;
  employeeData?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

const EmployeeRolesTab: React.FC<EmployeeRolesTabProps> = ({ employeeId, employeeStatus, employeeData }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<EmployeeRoles | null>(null);
  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [rolesError, setRolesError] = useState<{ message: string; traceId?: string } | null>(null);

  const [selectedRole, setSelectedRole] = useState<'EMPLOYEE' | 'MANAGER' | 'ADMIN'>('EMPLOYEE');
  const [selectedWorkAreaIds, setSelectedWorkAreaIds] = useState<number[]>([]);
  const [permissions, setPermissions] = useState({
    can_approve_absences: false,
    can_manage_shifts: false,
    can_view_compensation: false,
    require_compensation_pin: true,
    can_view_profile: false,
    can_view_documents: false,
  });

  useEffect(() => {
    loadRolesAndWorkAreas();
  }, [employeeId]);

  useEffect(() => {
    if (selectedRole === 'EMPLOYEE') {
      setSelectedWorkAreaIds([]);
      setPermissions({
        can_approve_absences: false,
        can_manage_shifts: false,
        can_view_compensation: false,
        require_compensation_pin: false,
        can_view_profile: false,
        can_view_documents: false,
      });
    }
  }, [selectedRole]);

  function normalizePermissions(p: {
    can_approve_absences: boolean;
    can_manage_shifts: boolean;
    can_view_compensation: boolean;
    require_compensation_pin: boolean;
    can_view_profile: boolean;
    can_view_documents: boolean;
  }) {
    const out = { ...p };
    if (!out.can_view_compensation) {
      out.require_compensation_pin = false;
    }
    return out;
  }

  async function loadRolesAndWorkAreas() {
    setLoading(true);
    setRolesError(null);

    const [rolesRes, waRes] = await Promise.allSettled([
      EmployeeRolesService.getRoles(employeeId),
      WorkAreaService.fetchWorkAreas(),
    ]);

    if (waRes.status === 'fulfilled') {
      setWorkAreas(waRes.value);
    } else {
      console.error('Failed to load work areas:', waRes.reason);
    }

    if (rolesRes.status === 'fulfilled') {
      setRoles(rolesRes.value);
      setSelectedRole(rolesRes.value.role || 'EMPLOYEE');
      setSelectedWorkAreaIds(rolesRes.value.work_area_ids || []);
      setPermissions(rolesRes.value.permissions);
      setRolesError(null);
    } else {
      console.error('Failed to load roles:', rolesRes.reason);
      const errorInfo = normalizeNetworkError(rolesRes.reason);
      setRolesError(errorInfo);
    }

    setLoading(false);
  }

  function validateForm(): boolean {
    const newErrors: { [key: string]: string } = {};

    if (selectedRole === 'MANAGER' && selectedWorkAreaIds.length === 0) {
      newErrors.work_areas = 'Manager role requires at least one work area';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSave() {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setErrors({});
    TelemetryService.logRolesSaveClicked(employeeId, selectedRole);

    try {
      let payload: any;

      if (selectedRole === 'EMPLOYEE') {
        payload = {
          role: 'EMPLOYEE',
          work_area_ids: [],
          permissions: {
            can_approve_absences: false,
            can_manage_shifts: false,
            can_view_compensation: false,
            require_compensation_pin: false,
            can_view_profile: false,
            can_view_documents: false,
          },
        };
      } else if (selectedRole === 'MANAGER') {
        payload = {
          role: 'MANAGER',
          work_area_ids: selectedWorkAreaIds,
          permissions: normalizePermissions(permissions),
        };
      } else {
        payload = {
          role: 'ADMIN',
          work_area_ids: [],
          permissions: {
            can_approve_absences: true,
            can_manage_shifts: true,
            can_view_compensation: true,
            require_compensation_pin: false,
            can_view_profile: true,
            can_view_documents: true,
          },
        };
      }

      await EmployeeRolesService.updateRoles(employeeId, payload);
      TelemetryService.logRolesSaveSucceeded(employeeId, selectedRole);
      toast.success('Employee roles updated successfully');
      await loadRolesAndWorkAreas();
    } catch (error: any) {
      console.error('Failed to update roles:', error);
      console.error('Error response:', error.response?.data);

      const errorCode = error.response?.data?.error || error.response?.data?.code || '';
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || error.message;
      const statusCode = error.response?.status;
      const traceId = error.response?.headers?.['x-trace-id'];
      const traceInfo = traceId ? ` (Trace-ID: ${traceId})` : '';

      TelemetryService.logRolesSaveFailed(employeeId, selectedRole, errorCode, statusCode);

      if (statusCode === 409 && errorCode === 'wa_scope_violation') {
        setErrors({ work_areas: 'Work area out of scope' });
        toast.error(`Selected work area is outside your scope${traceInfo}`);
        return;
      }

      if (statusCode === 404) {
        toast.error(`Employee not found or out of scope${traceInfo}`);
        return;
      }

      if (statusCode === 422) {
        toast.error(`${errorMessage || 'Validation error. Check your inputs.'}${traceInfo}`);
        return;
      }

      toast.error(`${errorMessage || 'Failed to update roles'}${traceInfo}`);
    } finally {
      setSaving(false);
    }
  }

  function toggleWorkArea(workAreaId: number) {
    setSelectedWorkAreaIds(prev =>
      prev.includes(workAreaId)
        ? prev.filter(id => id !== workAreaId)
        : [...prev, workAreaId]
    );
  }

  function getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      INVITED: colors.warning,
      WAITING_FOR_MANAGER: colors.warning,
      ACTIVE: colors.success,
      DEACTIVATED: colors.danger,
    };
    return colorMap[status] || colors.textLight;
  }

  function getStatusLabel(status: string): string {
    const labelMap: Record<string, string> = {
      INVITED: 'Invited',
      WAITING_FOR_MANAGER: 'Waiting',
      ACTIVE: 'Active',
      DEACTIVATED: 'Deactivated',
    };
    return labelMap[status] || status;
  }

  if (loading && workAreas.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>
        Loading roles...
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <div
        className="hover-card"
        style={{
          ...ps.card,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: colors.navy }}>
            Access & Roles
          </h3>
          {employeeStatus && (
            <span
              style={{
                padding: '4px 12px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600,
                color: colors.bgCard,
                background: getStatusColor(employeeStatus),
              }}
            >
              {getStatusLabel(employeeStatus)}
            </span>
          )}
        </div>

        {rolesError && (
          <div
            style={{
              padding: 16,
              background: colors.dangerLight,
              border: `1px solid ${colors.danger}`,
              borderRadius: 8,
              marginBottom: 24,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ fontSize: 20, lineHeight: 1 }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: colors.danger, marginBottom: 8, fontSize: 14 }}>
                  Failed to load roles
                </div>
                <div style={{ color: colors.danger, marginBottom: 12, fontSize: 14 }}>
                  {rolesError.message}
                </div>
                {rolesError.traceId && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: 8,
                      background: colors.bgCard,
                      borderRadius: 4,
                      fontSize: 12,
                      fontFamily: 'monospace',
                      color: colors.textMuted,
                    }}
                  >
                    Trace-ID: {rolesError.traceId}
                  </div>
                )}
                <button
                  onClick={loadRolesAndWorkAreas}
                  disabled={loading}
                  style={{
                    ...ps.buttonPrimary,
                    background: loading ? colors.border : ps.buttonPrimary.background,
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: colors.textBody }}>
            Role
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(['EMPLOYEE', 'MANAGER', 'ADMIN'] as const).map(role => (
              <label
                key={role}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: 12,
                  border: selectedRole === role ? `2px solid ${colors.navy}` : `1px solid ${colors.border}`,
                  borderRadius: 8,
                  background: selectedRole === role ? colors.infoLight : colors.bgCard,
                }}
              >
                <input
                  type="radio"
                  name="role"
                  value={role}
                  checked={selectedRole === role}
                  onChange={() => setSelectedRole(role)}
                  style={{ marginRight: 12 }}
                />
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500 }}>{role}</span>
                  {role === 'EMPLOYEE' && (
                    <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                      Base role with no additional permissions
                    </div>
                  )}
                  {role === 'MANAGER' && (
                    <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                      Requires at least one work area
                    </div>
                  )}
                  {role === 'ADMIN' && (
                    <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                      Full system access
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {selectedRole === 'MANAGER' && (
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: colors.textBody }}>
              Work Areas
            </label>
            <div
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                border: errors.work_areas ? `1px solid ${colors.danger}` : `1px solid ${colors.border}`,
                borderRadius: 8,
                padding: 12,
              }}
            >
              {workAreas.length === 0 ? (
                <div style={{ color: colors.textLight, fontSize: 14, textAlign: 'center', padding: 20 }}>
                  No work areas available
                </div>
              ) : (
                workAreas.map(wa => (
                  <label
                    key={wa.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: 8,
                      cursor: 'pointer',
                      borderRadius: 4,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedWorkAreaIds.includes(wa.id)}
                      onChange={() => toggleWorkArea(wa.id)}
                      style={{ marginRight: 8 }}
                    />
                    <span>{wa.name}</span>
                  </label>
                ))
              )}
            </div>
            {errors.work_areas && (
              <div style={{ color: colors.danger, fontSize: 13, marginTop: 6 }}>
                {errors.work_areas}
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', marginBottom: 12, fontSize: 14, fontWeight: 500, color: colors.textBody }}>
            Permissions
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={permissions.can_approve_absences}
                onChange={e => setPermissions(prev => ({ ...prev, can_approve_absences: e.target.checked }))}
                style={{ marginRight: 8 }}
              />
              <span>Approve absences</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={permissions.can_manage_shifts}
                onChange={e => setPermissions(prev => ({ ...prev, can_manage_shifts: e.target.checked }))}
                style={{ marginRight: 8 }}
              />
              <span>Manage shifts</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={permissions.can_view_compensation}
                onChange={e => setPermissions(prev => ({
                  ...prev,
                  can_view_compensation: e.target.checked,
                  require_compensation_pin: e.target.checked ? prev.require_compensation_pin : false
                }))}
                style={{ marginRight: 8 }}
              />
              <span>View compensation</span>
              {permissions.can_view_compensation && (
                <span style={{ marginLeft: 8, fontSize: 12, color: colors.textMuted }}>→ Require PIN</span>
              )}
            </label>

            {permissions.can_view_compensation && (
              <div style={{ marginLeft: 28 }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={permissions.require_compensation_pin}
                    onChange={e => setPermissions(prev => ({ ...prev, require_compensation_pin: e.target.checked }))}
                    style={{ marginRight: 8 }}
                  />
                  <span style={{ fontSize: 14 }}>Require PIN to view</span>
                </label>
              </div>
            )}

            {selectedRole === 'MANAGER' && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={permissions.can_view_profile}
                    onChange={e => setPermissions(prev => ({ ...prev, can_view_profile: e.target.checked }))}
                    style={{ marginRight: 8 }}
                  />
                  <span>View profile data</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={permissions.can_view_documents}
                    onChange={e => setPermissions(prev => ({ ...prev, can_view_documents: e.target.checked }))}
                    style={{ marginRight: 8 }}
                  />
                  <span>View documents</span>
                </label>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            className="hover-btn-primary"
            onClick={handleSave}
            disabled={saving || !!rolesError}
            style={{
              ...ps.buttonPrimary,
              background: saving || rolesError ? colors.border : ps.buttonPrimary.background,
              cursor: saving || rolesError ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>

          <button
            className="hover-btn-secondary"
            onClick={() => {
              setSelectedRole(roles?.role || 'EMPLOYEE');
              setSelectedWorkAreaIds(roles?.work_area_ids || []);
              setPermissions(roles?.permissions || {
                can_approve_absences: false,
                can_manage_shifts: false,
                can_view_compensation: false,
                require_compensation_pin: true,
                can_view_profile: false,
                can_view_documents: false,
              });
              setErrors({});
            }}
            disabled={saving}
            style={{
              ...ps.buttonSecondary,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeRolesTab;
